import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { ctaClicked } from '@/lib/analytics'

/**
 * Bottom-of-page CTA card. Same shape every explore page; the headline
 * + sub change per bucket so the close lands specific to what they
 * just read about.
 */
export function ClosingCTA({
  headline,
  subline,
  primaryCtaLabel = 'Start your squadron',
  primaryCtaTo = '/signup',
  secondaryCtaLabel = 'See pricing',
  secondaryCtaTo = '/pricing',
  location,
}: {
  headline: string
  subline: string
  primaryCtaLabel?: string
  primaryCtaTo?: string
  secondaryCtaLabel?: string
  secondaryCtaTo?: string
  /**
   * Which page this card closes — `squadrons`, `tournaments`, `live`,
   * `pricing`. Reported with the click so a close on the squadrons deep-dive
   * is distinguishable from one on pricing; they are very different intents.
   */
  location: string
}) {
  return (
    <section className="px-6 py-20 md:py-28">
      <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-bg to-secondary/10 p-10 text-center md:p-16">
        {/* Decorative corner accents — match the Hero's tactical frame. */}
        <span
          aria-hidden
          className="pointer-events-none absolute top-3 left-3 size-6 border-t border-l border-primary/40"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute top-3 right-3 size-6 border-t border-r border-primary/40"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-3 left-3 size-6 border-b border-l border-primary/40"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute right-3 bottom-3 size-6 border-r border-b border-primary/40"
        />

        <h2 className="font-display text-balance text-3xl leading-tight font-bold md:text-4xl lg:text-5xl">
          {headline}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-pretty text-base text-fg-muted md:text-lg">
          {subline}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild variant="primary" size="lg" className="w-full sm:w-auto">
            <Link
              to={primaryCtaTo}
              onClick={() =>
                ctaClicked('get_started', `closing_cta_${location}`, primaryCtaTo)
              }
            >
              {primaryCtaLabel}
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="font-display w-full sm:w-auto"
          >
            <Link
              to={secondaryCtaTo}
              onClick={() =>
                ctaClicked('secondary', `closing_cta_${location}`, secondaryCtaTo)
              }
            >
              {secondaryCtaLabel}
            </Link>
          </Button>
        </div>
        <p className="font-display mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] tracking-[0.2em] text-fg-subtle uppercase">
          <span>Free up to 10 members</span>
          <span aria-hidden className="text-fg-subtle/60">·</span>
          <span>No card required</span>
        </p>
      </div>
    </section>
  )
}
